import _ from 'lodash'
import { CaseSchema, DialogId, SwitchStepSchema } from '@feedyou/schema'

import { confirm } from '../../../lib/bootbox'
import { CREATE_NEW_DIALOG, stepAction } from '../actions'
import { FeedbotThunk } from '../../../store/types'
import { sanitizeText } from '../../../lib/sanitizeText'
import { onClickWithBotMutationCheck } from '../../Step/StepService'
import {
  getDefaultCaseTitle,
  getPromptedTitleAndValue,
  suggestCaseSubdialogId
} from './CasesService'
import { getSelectedMutationsSelector } from '../../../store/selectedMutations/selectors'
import { sanitizeTitle } from './utils'
import { getNextUniqueDialogId } from '../../../store/flow/thunks/unlinkDialog'
import { createNewDialog } from '../../../store/flow/thunks/createNewDialog'
import { dialogsSelector } from '../../../store/flow/selectors/dialogsSelector'

export const actionTypes = {
  SET_CASE: 'SET_CASE',
  SET_DEFAULT: 'SET_DEFAULT',
  DELETE_DEFAULT: 'DELETE_DEFAULT',
  SET_CASE_DIALOG: 'SET_CASE_DIALOG',
  DELETE_CASE: 'DELETE_CASE',
  SET_CONDITION: 'SET_CONDITION'
} as const

export function setCase(item: CaseSchema) {
  const itemCopy = _.cloneDeep(item)
  itemCopy.title = sanitizeText(itemCopy.title)
  itemCopy.value = sanitizeText(itemCopy.value)

  return { type: actionTypes.SET_CASE, payload: itemCopy }
}

export function modifyDefault(patch: NonNullable<Partial<SwitchStepSchema['default']>>) {
  return {
    type: actionTypes.SET_DEFAULT,
    payload: sanitizeTitle(patch)
  }
}

export function deleteDefaultCase() {
  return { type: actionTypes.DELETE_DEFAULT }
}

export function setCaseDialog(caseId: string, dialogId: DialogId | undefined) {
  return {
    type: actionTypes.SET_CASE_DIALOG,
    payload: { caseId, dialogId }
  }
}

export function deleteCase(itemId: string) {
  return { type: actionTypes.DELETE_CASE, payload: itemId }
}

export function setCondition(item: CaseSchema) {
  const itemCopy = _.cloneDeep(item)
  itemCopy.title = sanitizeText(itemCopy.title)
  itemCopy.value = sanitizeText(itemCopy.value)

  return { type: actionTypes.SET_CONDITION, payload: itemCopy }
}

export function addCase(stepId: string): FeedbotThunk {
  return async (dispatch, getState) => {
    const selectedMutations = getSelectedMutationsSelector(getState())
    await onClickWithBotMutationCheck('case', selectedMutations, dispatch, async () => {
      const prompt = await getPromptedTitleAndValue()
      if (!prompt) {
        return
      }

      const { title, value } = prompt
      dispatch(stepAction(stepId, setCase({ title, value })))
    })
  }
}

export function addDefaultCase(stepId: string): FeedbotThunk {
  return async (dispatch, getState) => {
    const selectedMutations = getSelectedMutationsSelector(getState())
    await onClickWithBotMutationCheck('default case', selectedMutations, dispatch, async () => {
      const promptTitle = await getDefaultCaseTitle('Default')

      if (promptTitle) {
        dispatch(stepAction(stepId, modifyDefault({ title: promptTitle })))
      }
    })
  }
}

export function caseInlineEditClick(
  caseId: string,
  switchId: string,
  itemTitle?: string,
  itemValue?: string
): FeedbotThunk {
  return async dispatch => {
    const prompt = await getPromptedTitleAndValue(itemTitle, itemValue)
    if (!prompt) {
      return
    }

    const { title, value } = prompt
    dispatch(stepAction(switchId, setCase({ id: caseId, title, value })))
  }
}

export function confirmDeleteCase(caseId: string, switchId: string): FeedbotThunk {
  return async dispatch => {
    const didUserConfirm = await confirm(
      'Do you really want to delete this case? It cannot be undone.'
    )

    if (didUserConfirm) {
      dispatch(stepAction(switchId, deleteCase(caseId)))
    }
  }
}

export const setDefaultCaseDialogThunk =
  (
    dialogId: DialogId | typeof CREATE_NEW_DIALOG | undefined,
    step: SwitchStepSchema
  ): FeedbotThunk =>
  (dispatch, getState) => {
    if (dialogId === CREATE_NEW_DIALOG) {
      const defaultId = suggestCaseSubdialogId(step.id, step.title, step.defaultCase?.title, 0)
      const nextUniqueDialogId = getNextUniqueDialogId(defaultId, dialogsSelector(getState()))
      dispatch(createNewDialog(nextUniqueDialogId))
      dispatch(stepAction(step.id, modifyDefault({ dialog: nextUniqueDialogId })))
      return
    }

    dispatch(stepAction(step.id, modifyDefault({ dialog: dialogId })))
  }

export const setCaseDialogThunk =
  (
    dialogId: DialogId | typeof CREATE_NEW_DIALOG | undefined,
    step: SwitchStepSchema,
    caseItem: CaseSchema,
    caseIndex: number
  ): FeedbotThunk =>
  (dispatch, getState) => {
    if (dialogId === CREATE_NEW_DIALOG) {
      const defaultId = suggestCaseSubdialogId(step.id, step.title, caseItem.title, caseIndex)
      const nextUniqueDialogId = getNextUniqueDialogId(defaultId, dialogsSelector(getState()))
      dispatch(createNewDialog(nextUniqueDialogId))
      dispatch(stepAction(step.id, setCaseDialog(caseItem.id!, nextUniqueDialogId)))
      return
    }

    dispatch(stepAction(step.id, setCaseDialog(caseItem.id!, dialogId)))
  }

export type Action = ReturnType<
  | typeof setCase
  | typeof modifyDefault
  | typeof deleteDefaultCase
  | typeof setCaseDialog
  | typeof deleteCase
  | typeof setCondition
>
